import React, { useState, useRef, useEffect, useCallback } from 'react';

interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CustomImageCropperProps {
  image: string;
  onCropComplete: (croppedAreaPixels: Area) => void;
  containerSize?: number; // Tamaño del contenedor cuadrado en píxeles
}

export const CustomImageCropper: React.FC<CustomImageCropperProps> = ({
  image,
  onCropComplete,
  containerSize = 300,
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);

  const isDragging = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const panStart = useRef({ x: 0, y: 0 });

  // Load image dimensions
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
      setZoom(1);
      setPan({ x: 0, y: 0 });
    };
    img.src = image;
  }, [image]);

  const constrainPan = useCallback(
    (newPan: { x: number; y: number }, currentZoom: number) => {
      if (!imageSize) return { x: 0, y: 0 };

      const baseScale = Math.max(containerSize / imageSize.width, containerSize / imageSize.height);
      const totalScale = baseScale * currentZoom;

      const currentW = imageSize.width * totalScale;
      const currentH = imageSize.height * totalScale;

      const maxX = Math.max(0, (currentW - containerSize) / 2);
      const maxY = Math.max(0, (currentH - containerSize) / 2);

      return {
        x: Math.min(Math.max(newPan.x, -maxX), maxX),
        y: Math.min(Math.max(newPan.y, -maxY), maxY),
      };
    },
    [imageSize, containerSize]
  );

  useEffect(() => {
    // When zoom changes, we must constrain pan so we don't end up out of bounds
    setPan((prevPan) => constrainPan(prevPan, zoom));
  }, [zoom, constrainPan]);

  useEffect(() => {
    // Report crop area whenever pan or zoom or imageSize changes
    if (!imageSize) return;

    const baseScale = Math.max(containerSize / imageSize.width, containerSize / imageSize.height);
    const totalScale = baseScale * zoom;

    const currentW = imageSize.width * totalScale;
    const currentH = imageSize.height * totalScale;

    // Center of container is containerSize/2
    // Top-left of the image inside the container
    const imgX = containerSize / 2 + pan.x - currentW / 2;
    const imgY = containerSize / 2 + pan.y - currentH / 2;

    // Crop box is from 0,0 to containerSize,containerSize in container coordinates
    // Translate that to original image coordinates
    const pixelCropX = -imgX / totalScale;
    const pixelCropY = -imgY / totalScale;
    const pixelCropWidth = containerSize / totalScale;
    const pixelCropHeight = containerSize / totalScale;

    onCropComplete({
      x: Math.max(0, pixelCropX),
      y: Math.max(0, pixelCropY),
      width: pixelCropWidth,
      height: pixelCropHeight,
    });
  }, [pan, zoom, imageSize, containerSize, onCropComplete]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    isDragging.current = true;
    dragStart.current = { x: e.clientX, y: e.clientY };
    panStart.current = { ...pan };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current) return;
    const deltaX = e.clientX - dragStart.current.x;
    const deltaY = e.clientY - dragStart.current.y;
    setPan(constrainPan({ x: panStart.current.x + deltaX, y: panStart.current.y + deltaY }, zoom));
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    isDragging.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch (err) {
      // Ignorar errores si el puntero ya se liberó
    }
  };

  if (!imageSize) return null;

  const baseScale = Math.max(containerSize / imageSize.width, containerSize / imageSize.height);
  const totalScale = baseScale * zoom;

  return (
    <div className="flex flex-col gap-4 w-full items-center">
      <div
        className="relative overflow-hidden bg-black/40 touch-none select-none"
        style={{
          width: containerSize,
          height: containerSize,
          borderRadius: '1rem',
          cursor: isDragging.current ? 'grabbing' : 'grab',
          touchAction: 'none'
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
      >
        <img
          src={image}
          alt="Crop"
          className="absolute max-w-none select-none pointer-events-none"
          style={{
            width: imageSize.width * totalScale,
            height: imageSize.height * totalScale,
            left: '50%',
            top: '50%',
            transform: `translate(calc(-50% + ${pan.x}px), calc(-50% + ${pan.y}px))`,
          }}
          draggable={false}
        />
        
        {/* Guía visual (cuadrícula y esquinas) */}
        <div className="absolute inset-0 pointer-events-none opacity-50 z-10">
          <div className="absolute inset-0 border border-white/40 rounded-2xl" />
          
          <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/30" />
          <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/30" />
          <div className="absolute top-1/3 left-0 right-0 h-px bg-white/30" />
          <div className="absolute top-2/3 left-0 right-0 h-px bg-white/30" />
          
          {/* Esquinas remarcadas */}
          <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-white rounded-tl-[1rem]" />
          <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-white rounded-tr-[1rem]" />
          <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-white rounded-bl-[1rem]" />
          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-white rounded-br-[1rem]" />
        </div>
      </div>

      <div className="flex items-center gap-4 w-full max-w-[300px]">
        <span className="text-[10px] font-black uppercase opacity-40 shrink-0 text-white">Zoom</span>
        <input
          type="range"
          value={zoom}
          min={1}
          max={3}
          step={0.05}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="flex-1 accent-[#eab308]"
        />
      </div>
    </div>
  );
};
